import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GAME_WORDS } from '../../data/gameWords';
import { getDateKey } from '../WordMaster/wordMasterUtils';
import { useAuth } from '../../context/AuthContext';
import { recordWordRungsDailyCompletion } from '../../firebase/wordRungsService';
import {
  WORD_RUNGS_WORD_LENGTH,
  pickPracticePuzzle,
  resolveDailyPuzzle,
  wordRungsDailySeed,
  oneLetterDifference,
} from './wordRungsUtils';
import CozyPlayShell from '../../components/CozyPlayShell/CozyPlayShell';
import { HowToPlay } from './HowToPlay';
import './WordRungs.css';

const HOW_TO_KEY = 'word_rungs_how_to_play_dismissed_v1';

function WordRungsLadder({ words, highlightGoal = false }) {
  if (!words.length) return null;
  return (
    <div className="word-rungs__ladder">
      {words.map((word, i) => (
        <React.Fragment key={`${i}-${word}`}>
          <div
            className={`word-rungs__rung${
              highlightGoal && i === words.length - 1 ? ' word-rungs__rung--goal' : ''
            }`}
          >
            <span className="word-rungs__step">{i + 1}</span>
            <span className="word-rungs__rung-word">{word}</span>
            {i === 0 && <span className="word-rungs__rung-tag">Start</span>}
            {highlightGoal && i === words.length - 1 && (
              <span className="word-rungs__rung-tag">Goal</span>
            )}
          </div>
          {i < words.length - 1 && (
            <div className="word-rungs__rung-arrow" aria-hidden="true">
              ↓
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function buildFiveLetterDict() {
  const s = new Set();
  for (const w of GAME_WORDS) {
    if (w.length === WORD_RUNGS_WORD_LENGTH) {
      s.add(w.toUpperCase());
    }
  }
  return s;
}

export default function WordRungs() {
  const { user, refreshProfile } = useAuth();
  const dict = useMemo(() => buildFiveLetterDict(), []);
  const [practice, setPractice] = useState(false);
  const [puzzle, setPuzzle] = useState(null);
  const [chain, setChain] = useState([]);
  const [won, setWon] = useState(false);
  const [finalElapsedSeconds, setFinalElapsedSeconds] = useState(null);
  const [saved, setSaved] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const startedAtMsRef = useRef(null);
  const dailySnapRef = useRef(null);
  const practiceSnapRef = useRef(null);
  const activeDailyBoardDateKeyRef = useRef(null);
  const dictInitDoneRef = useRef(false);
  const [typing, setTyping] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [badMessage, setBadMessage] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [, setTimerTick] = useState(0);

  const purgeStaleDailyRungsSnap = useCallback(() => {
    const today = getDateKey();
    const ds = dailySnapRef.current;
    if (ds?.dailyDateKey != null && ds.dailyDateKey !== today) {
      dailySnapRef.current = null;
    }
    if (activeDailyBoardDateKeyRef.current != null && activeDailyBoardDateKeyRef.current !== today) {
      activeDailyBoardDateKeyRef.current = null;
    }
  }, []);

  const loadFreshRungsBoard = useCallback((p, mode) => {
    if (!p) return;
    if (mode === 'practice') practiceSnapRef.current = null;
    else dailySnapRef.current = null;
    setPuzzle(p);
    setChain([p.start]);
    setWon(false);
    setFinalElapsedSeconds(null);
    setSaved(false);
    setStartedAt(null);
    startedAtMsRef.current = null;
    setTyping('');
    setBadMessage(null);
    setSaveMessage(null);
    activeDailyBoardDateKeyRef.current = mode === 'daily' ? getDateKey() : null;
  }, []);

  const restoreRungsSnap = useCallback((snap) => {
    setPuzzle(snap.puzzle);
    setChain(snap.chain);
    setWon(snap.won);
    setSaved(snap.saved);
    setStartedAt(snap.startedAtState);
    startedAtMsRef.current = snap.startedAtMs;
    setTyping(snap.typing);
    setBadMessage(snap.badMessage);
    setSaveMessage(snap.saveMessage);
    setFinalElapsedSeconds(snap.finalElapsedSeconds);
    activeDailyBoardDateKeyRef.current = snap.dailyDateKey ?? null;
  }, []);

  const applyRungsModeTap = useCallback(
    (nextPractice) => {
      if (nextPractice === practice) return;

      if (practice) {
        const p = puzzle;
        const snap =
          p != null
            ? {
                puzzle: p,
                chain: [...chain],
                won,
                saved,
                startedAtMs: startedAtMsRef.current,
                startedAtState: startedAt,
                typing,
                badMessage,
                saveMessage,
                finalElapsedSeconds,
                dailyDateKey: null,
              }
            : null;
        if (snap != null) practiceSnapRef.current = snap;
      } else {
        const p = puzzle;
        const snap =
          p != null
            ? {
                puzzle: p,
                chain: [...chain],
                won,
                saved,
                startedAtMs: startedAtMsRef.current,
                startedAtState: startedAt,
                typing,
                badMessage,
                saveMessage,
                finalElapsedSeconds,
                dailyDateKey: getDateKey(),
              }
            : null;
        if (snap != null) dailySnapRef.current = snap;
      }

      setPractice(nextPractice);

      if (nextPractice) {
        const snap = practiceSnapRef.current;
        if (snap) restoreRungsSnap(snap);
        else {
          let p = null;
          for (let i = 0; i < 40 && !p; i++) {
            p = pickPracticePuzzle(dict);
          }
          loadFreshRungsBoard(p, 'practice');
        }
      } else {
        purgeStaleDailyRungsSnap();
        const daily = resolveDailyPuzzle(dict, wordRungsDailySeed());
        const snap = dailySnapRef.current;
        if (
          snap != null &&
          snap.dailyDateKey === getDateKey() &&
          daily != null &&
          snap.puzzle.puzzleKey === daily.puzzleKey
        ) {
          restoreRungsSnap(snap);
        } else {
          dailySnapRef.current = null;
          loadFreshRungsBoard(daily, 'daily');
        }
      }
    },
    [
      dict,
      practice,
      purgeStaleDailyRungsSnap,
      puzzle,
      chain,
      won,
      saved,
      startedAt,
      typing,
      badMessage,
      saveMessage,
      finalElapsedSeconds,
      restoreRungsSnap,
      loadFreshRungsBoard,
    ]
  );

  useEffect(() => {
    if (dict.size < 2 || dictInitDoneRef.current) return;
    dictInitDoneRef.current = true;
    const seed = wordRungsDailySeed();
    loadFreshRungsBoard(resolveDailyPuzzle(dict, seed), 'daily');
  }, [dict, loadFreshRungsBoard]);

  useEffect(() => {
    if (!startedAt || won) return undefined;
    const id = window.setInterval(() => setTimerTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [startedAt, won]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(HOW_TO_KEY) === '1') return;
    setShowHowToPlay(true);
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      purgeStaleDailyRungsSnap();
      if (
        practice ||
        activeDailyBoardDateKeyRef.current == null ||
        activeDailyBoardDateKeyRef.current === getDateKey()
      )
        return;
      const seed = wordRungsDailySeed();
      loadFreshRungsBoard(resolveDailyPuzzle(dict, seed), 'daily');
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [practice, purgeStaleDailyRungsSnap, dict, loadFreshRungsBoard]);

  const handleHowToPlayClose = () => {
    setShowHowToPlay(false);
    try {
      window.localStorage.setItem(HOW_TO_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const elapsedLive =
    startedAtMsRef.current != null ? Math.floor((Date.now() - startedAtMsRef.current) / 1000) : 0;
  const elapsedDisplay = won && finalElapsedSeconds != null ? finalElapsedSeconds : elapsedLive;

  const addStep = async () => {
    if (!puzzle || won) return;
    const raw = typing.trim().toUpperCase();
    if (raw.length !== WORD_RUNGS_WORD_LENGTH) {
      setBadMessage(`Use exactly ${WORD_RUNGS_WORD_LENGTH} letters`);
      return;
    }
    if (!dict.has(raw)) {
      setBadMessage('Not in word list');
      return;
    }
    const last = chain[chain.length - 1].toUpperCase();
    if (!oneLetterDifference(last, raw)) {
      setBadMessage('Must differ by exactly one letter from the last word');
      return;
    }
    if (chain.some((w) => w.toUpperCase() === raw)) {
      setBadMessage('That word is already in your chain');
      return;
    }

    if (startedAtMsRef.current == null) {
      const t = Date.now();
      startedAtMsRef.current = t;
      setStartedAt(t);
    }

    const nextChain = [...chain, raw];
    const complete = raw === puzzle.end.toUpperCase();
    setChain(nextChain);
    setBadMessage(null);
    setTyping('');
    if (complete) {
      const sec = Math.floor((Date.now() - (startedAtMsRef.current ?? Date.now())) / 1000);
      setFinalElapsedSeconds(sec);
      setWon(true);
      if (!practice && !saved) {
        await saveCompletion(nextChain, sec);
      }
    }
  };

  const saveCompletion = async (finalChain, elapsedSecondsArg) => {
    if (saved || practice || !puzzle) return;
    if (!user) {
      setSaveMessage('Sign in to save scores and streaks.');
      return;
    }
    const elapsedSeconds = elapsedSecondsArg ?? 0;
    try {
      await recordWordRungsDailyCompletion({
        uid: user.uid,
        dateId: getDateKey(),
        puzzleKey: puzzle.puzzleKey,
        startWord: puzzle.start,
        endWord: puzzle.end,
        chain: finalChain.map((w) => w.toUpperCase()),
        optimalTransitions: puzzle.optimalTransitions,
        elapsedSeconds,
        displayName: user.displayName || user.email?.split('@')[0],
      });
      setSaved(true);
      setSaveMessage('Saved to leaderboard.');
      await refreshProfile();
    } catch (e) {
      setSaveMessage(e?.message || 'Could not save');
    }
  };

  const undo = () => {
    if (won || chain.length <= 1) return;
    setChain(chain.slice(0, -1));
    setBadMessage(null);
  };

  const newPractice = () => {
    if (!dict.size) return;
    let p = null;
    for (let i = 0; i < 40 && !p; i++) {
      p = pickPracticePuzzle(dict);
    }
    loadFreshRungsBoard(p, 'practice');
  };

  if (dict.size < 2) {
    return <p className="word-rungs__error">Need more 5-letter words in the dictionary.</p>;
  }
  if (!puzzle) {
    return <p className="word-rungs__loading">Building puzzle…</p>;
  }

  return (
    <CozyPlayShell
      className="word-rungs"
      title="Word Rungs"
      mode={practice ? 'practice' : 'daily'}
      onModeChange={(m) => applyRungsModeTap(m === 'practice')}
      onHowToPlay={() => setShowHowToPlay(true)}
      subtitle="Climb one letter at a time"
      meta={
        <>
          <span className="word-rungs__time">
            {won
              ? `Done in ${chain.length - 1} steps · ${elapsedDisplay}s`
              : `Time: ${startedAt == null ? '—' : `${elapsedDisplay}s`}`}
          </span>
          {!practice && <span className="word-rungs__date">{getDateKey()}</span>}
        </>
      }
    >
      {showHowToPlay && <HowToPlay onClose={handleHowToPlayClose} />}

      <div className="word-rungs__endpoints">
        <div className="word-rungs__endpoint">
          <p className="word-rungs__label">Start</p>
          <p className="word-rungs__word">{puzzle.start}</p>
        </div>
        <div className="word-rungs__endpoint word-rungs__endpoint--goal">
          <p className="word-rungs__label">Goal</p>
          <p className="word-rungs__word word-rungs__word--goal">{puzzle.end}</p>
        </div>
      </div>

      {!won ? (
        <>
          <p className="word-rungs__label">Your ladder ({chain.length} words)</p>
          <WordRungsLadder words={chain} />
          <input
            className="word-rungs__input"
            maxLength={WORD_RUNGS_WORD_LENGTH}
            value={typing}
            onChange={(e) => {
              setTyping(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, WORD_RUNGS_WORD_LENGTH));
              if (badMessage) setBadMessage(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addStep();
            }}
            placeholder="Next word"
            spellCheck={false}
            aria-label="Next word in chain"
          />
          {badMessage ? (
            <div className="word-rungs__soft-msg" role="status">
              {badMessage}
            </div>
          ) : null}
          <div className="word-rungs__actions">
            <button type="button" className="word-rungs__btn word-rungs__btn--primary" onClick={() => void addStep()}>
              Add step
            </button>
            <button type="button" className="word-rungs__btn word-rungs__btn--secondary" onClick={undo}>
              Undo
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="word-rungs__label">Your ladder</p>
          <WordRungsLadder words={chain} highlightGoal />
          <p className="word-rungs__optimal">
            Shortest path: {puzzle.optimalTransitions} step{puzzle.optimalTransitions === 1 ? '' : 's'}
          </p>
          {!practice && !user && (
            <p className="word-rungs__save-msg">
              <Link to="/login">Sign in</Link> to save scores and streaks.
            </p>
          )}
          {saveMessage && <p className="word-rungs__save-msg">{saveMessage}</p>}
        </>
      )}

      {practice && (
        <button type="button" className="word-rungs__btn word-rungs__btn--secondary" onClick={newPractice}>
          New practice puzzle
        </button>
      )}
    </CozyPlayShell>
  );
}
